<template>
  <div class="grade-level-summary">
    <h2 class="text-h6 font-weight-bold text-secondary mb-3">
      Gateway Ratings Summary
    </h2>
    <div>
      <div class="grades-grid">
        <div
          v-for="grade in processedGrades"
          :key="grade.name"
          class="grade-column"
        >
          <v-card class="pa-4">
            <v-row align="center" no-gutters class="pb-3">
              <v-col cols="6">
                <h4 class="font-weight-bold text-secondary">
                  {{ formatGradeName(grade.name) }}
                </h4>
              </v-col>
              <v-col cols="6" align="right">
                <v-btn
                  variant="text"
                  color="primary"
                  class="font-weight-bold text-uppercase pa-0 view-report-btn"
                  density="comfortable"
                  @click="navigateToGradeReport(grade)"
                >
                  View Report
                </v-btn>
              </v-col>
            </v-row>
            <v-row no-gutters>
              <v-col cols="12">
                <v-card
                  v-for="gateway in grade.gateways"
                  :key="gateway.slug"
                  :class="[
                    getGatewayCardClass(gateway.review_status),
                    'p-0',
                    { 'mb-3': !isLastGateway(gateway, grade.gateways) },
                  ]"
                >
                  <v-row no-gutters class="px-3 pt-3 pb-2">
                    <v-col cols="8">
                      <div class="mb-1">
                        <h5 class="font-weight-bold mb-4">
                          {{ gateway.label }}
                        </h5>
                        <h5 class="font-weight-regular">
                          {{ gateway.title }}
                        </h5>
                      </div>
                    </v-col>
                    <v-col cols="4" class="align-self-end text-right">
                      <h1 class="score-display">
                        {{ gateway.formattedScore }}
                      </h1>
                    </v-col>
                  </v-row>
                  <div class="card-footer">
                    <span class="card-footer-text">{{
                      mapStatusText(gateway.review_status?.title)
                    }}</span>
                  </div>
                </v-card>
              </v-col>
            </v-row>
          </v-card>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
  import { useRouter } from 'vue-router';
  import { formatGradeName } from '../../lib/grades';
  import { mapStatusText, formatGatewayScore } from '../../lib/status-utils';
  import { computed } from 'vue';

  const router = useRouter();

  interface ReviewStatus {
    slug: string;
    name: string;
    title: string;
  }

  interface Gateway {
    slug: string;
    label: string;
    name: string;
    title: string;
    score: string;
    review_status: ReviewStatus;
    order: number;
    failed_non_negotiable: boolean;
    formattedScore: string;
  }

  interface Grade {
    name: string;
    reportUrl?: string;
    gateways: Gateway[];
  }

  interface Props {
    grades: Grade[];
  }

  const props = defineProps<Props>();

  const processedGrades = computed(() => {
    return props.grades.map((grade) => ({
      ...grade,
      gateways: grade.gateways.map((gateway) => ({
        ...gateway,
        formattedScore: formatGatewayScore(
          gateway.score,
          gateway.review_status?.slug,
        ),
      })),
    }));
  });

  function getGatewayCardClass(status: ReviewStatus | undefined): string {
    if (!status) return 'grey-card';

    const statusMap: Record<string, string> = {
      meets: 'green-card',
      'partially-meets': 'orange-card',
      'does-not-meet': 'red-card',
      'does-not-exist': 'grey-card',
    };
    return statusMap[status.slug] || 'grey-card';
  }

  function isLastGateway(
    gateway: Gateway,
    currentGradeGateways: Gateway[],
  ): boolean {
    if (!currentGradeGateways || currentGradeGateways.length === 0) return true;
    return gateway === currentGradeGateways[currentGradeGateways.length - 1];
  }

  function navigateToGradeReport(grade: Grade) {
    if (grade.reportUrl) {
      // Extract the route segments from the reportUrl
      // Expected format: "/reports/detail/{reportSlug}/{gradeSlug}"
      const urlParts = grade.reportUrl.split('/');
      if (urlParts.length >= 5) {
        const reportSlug = urlParts[3];
        const gradeSlug = urlParts[4];

        router.push({
          name: 'report-grade-overview',
          params: {
            id: reportSlug,
            grade: gradeSlug,
          },
        });
      }
    }
  }
</script>

<style lang="scss" scoped>
  @use '../../styles/abstracts/variables' as *;
  @use '../../styles/abstracts/mixins' as *;

  .grade-level-summary {
    h4 {
      font-size: 16px;
    }

    .view-report-btn {
      font-size: 10px !important;
    }

    .score-display {
      white-space: nowrap;
    }

    .grades-grid {
      display: grid;
      grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
      gap: 8px;

      @media (min-width: 960px) {
        grid-template-columns: repeat(3, 1fr);
      }
    }

    .grade-column {
      width: 100%;
    }

    .v-card {
      border: 1px solid rgba(var(--v-theme-border-light), 1);

      &.green-card {
        @include gateway-card-base;
        @include gateway-card-green;
      }

      &.orange-card {
        @include gateway-card-base;
        @include gateway-card-orange;
      }

      &.red-card {
        @include gateway-card-base;
        @include gateway-card-red;
      }

      &.grey-card {
        @include gateway-card-base;
        @include gateway-card-grey;
      }

      .card-footer {
        padding: 2px 12px;
        text-align: left;
        border-bottom-left-radius: inherit;
        border-bottom-right-radius: inherit;

        .card-footer-text {
          font-size: 12px;
          font-weight: bold;
          text-transform: uppercase;
        }
      }
    }
  }
</style>
